import React from 'react';
import {
  List,
  Datagrid,
  TextField,
  DateField,
  Show,
  NumberField,
  FunctionField,
  Filter,
  TextInput,
  SelectInput,
  TabbedShowLayout,
  Tab,
} from 'react-admin';
import { Chip, Card, CardContent, Typography, Grid, Box, Link as MuiLink } from '@mui/material';

// Prefer env var; fallback matches backend default
const EXPLORER_BASE = (process.env.REACT_APP_BLOCK_EXPLORER_URL || 'https://blockscout.testnet-v2.atleta.network').replace(/\/+$/, '');

const ClaimTransactionFilter = (props: any) => (
  <Filter {...props}>
    <TextInput label="Address" source="address" alwaysOn />
    <SelectInput
      label="Status"
      source="status"
      choices={[
        { id: 'pending', name: 'Pending' },
        { id: 'confirmed', name: 'Confirmed' },
        { id: 'failed', name: 'Failed' },
        { id: 'reverted', name: 'Reverted' },
      ]}
    />
  </Filter>
);

const formatTokenAmount = (wei: string) => {
  try {
    const amount = Number(BigInt(wei) / BigInt(10 ** 18));
    return amount.toLocaleString() + ' ATLA';
  } catch {
    return '0 ATLA';
  }
};

const StatusField = ({ record }: any) => {
  const statusColors: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
    confirmed: 'success',
    pending: 'warning',
    failed: 'error',
    reverted: 'error',
  };

  return (
    <Chip
      label={record?.status || 'unknown'}
      color={statusColors[record?.status] || 'default'}
      size="small"
    />
  );
};

export const ClaimTransactionList = () => (
  <List filters={<ClaimTransactionFilter />}>
    <Datagrid rowClick="show">
      <TextField source="txHash" label="Transaction Hash" />
      <TextField source="address" label="Wallet Address" />
      <FunctionField
        label="Amount"
        render={(record: any) => formatTokenAmount(record.amount || '0')}
      />
      <FunctionField
        label="Status"
        render={(record: any) => <StatusField record={record} />}
      />
      <DateField source="submittedAt" label="Submitted" showTime />
      <DateField source="confirmedAt" label="Confirmed" showTime />
    </Datagrid>
  </List>
);

const TransactionSummary = ({ record }: any) => {
  const explorerUrl = record?.txHash ? `${EXPLORER_BASE}/tx/${record.txHash}` : undefined;
  
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ bgcolor: 'primary.main', color: 'white' }}>
          <CardContent>
            <Typography variant="caption">Transaction Hash</Typography>
            <Typography variant="body2" sx={{ wordBreak: 'break-all', fontSize: '0.75rem' }}>
              {record?.txHash || 'N/A'}
            </Typography>
            {explorerUrl && (
              <MuiLink href={explorerUrl} target="_blank" rel="noopener" sx={{ color: 'white', textDecoration: 'underline' }}>
                View on Explorer →
              </MuiLink>
            )}
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ bgcolor: 'success.main', color: 'white' }}>
          <CardContent>
            <Typography variant="caption">Amount Claimed</Typography>
            <Typography variant="h5">{formatTokenAmount(record?.amount || '0')}</Typography>
          </CardContent>
        </Card>
      </Grid>
      <Grid size={{ xs: 12, md: 4 }}>
        <Card sx={{ 
          bgcolor: record?.status === 'confirmed' ? 'success.light' : 
                   record?.status === 'pending' ? 'warning.light' : 'error.light' 
        }}>
          <CardContent>
            <Typography variant="caption">Status</Typography>
            <Typography variant="h5">
              <StatusField record={record} />
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

export const ClaimTransactionShow = () => (
  <Show>
    <TabbedShowLayout>
      <Tab label="Overview">
        <FunctionField render={(record: any) => <TransactionSummary record={record} />} />
        <TextField source="address" label="Wallet Address" />
        <NumberField source="blockNumber" label="Block Number" />
        <NumberField source="confirmations" label="Confirmations" />
        <DateField source="submittedAt" label="Submitted At" showTime />
        <DateField source="confirmedAt" label="Confirmed At" showTime />
      </Tab>
      <Tab label="Gas Details">
        <TextField source="gasUsed" label="Gas Used" />
        <FunctionField
          label="Total Gas Cost"
          render={(record: any) => {
            if (!record.gasUsed) return 'N/A';
            return `${record.gasUsed} wei`;
          }}
        />
      </Tab>
      <Tab label="Network Info">
        <FunctionField
          label="Network"
          render={(record: any) => (
            <Box sx={{ p: 2 }}>
              <Typography variant="h6">Network Information</Typography>
              <Typography variant="body1">
                Name: {record.network?.name || 'Unknown'}
              </Typography>
              <Typography variant="body1">
                Chain ID: {record.network?.chainId || 'N/A'}
              </Typography>
            </Box>
          )}
        />
        <TextField source="errorMessage" label="Error Message" />
      </Tab>
      <Tab label="Raw Data">
        <FunctionField
          label="Full Transaction Data"
          render={(record: any) => (
            <pre style={{ background: '#f5f5f5', padding: '10px', borderRadius: '4px', overflow: 'auto' }}>
              {JSON.stringify(record, null, 2)}
            </pre>
          )}
        />
      </Tab>
    </TabbedShowLayout>
  </Show>
);
